// Company signals (202609301900) — a dated fact attached to a company.
//
//   CompanySignalsSection   the company page block: live signals, newest and
//                           strongest first, with the pitch line and Done /
//                           Dismiss. Renders nothing when the company has none.
// The queue view (home Brief strip, ranked list) reads the same API.

const SIGNAL_KIND = {
  hold_reengaged: { label: 'Wrote in',         hue: 'green'  },
  timing_note:    { label: 'Owner timing',     hue: 'amber'  },
  hold_checkin:   { label: 'Hold check-in',    hue: 'amber'  },
  went_dark:      { label: 'Went dark',        hue: 'rose'   },
  nearby_sale:    { label: 'Nearby sale',      hue: 'teal'   },
  nearby_listing: { label: 'Nearby listing',   hue: 'violet' },
  buyer_demand:   { label: 'Buyer demand',     hue: 'blue'   },
  web_visit:      { label: 'Website visit',    hue: 'teal'   },
};
const signalKind = (k) => SIGNAL_KIND[k] || { label: k, hue: 'slate' };
const isHoldKind = (k) => k === 'hold_reengaged' || k === 'hold_checkin';
// An owner who stopped replying (went_dark's second rule) carries the touches
// that went unanswered; it opens the same "how it got here" card.
const isSilent = (s) => !!(s && s.evidence && s.evidence.trigger === 'owner_silent');
const hasWhyCard = (s) => isHoldKind(s.kind) || isSilent(s);
const TOUCH_LABEL = { email: 'Email', call: 'Call', voicemail: 'Voicemail', text: 'Text' };
const shortDay = (iso) => {
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso || '') ? `${iso}T00:00:00` : iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};
const dueLabel = (due) => {
  if (!due) return null;
  const d = new Date(due + 'T00:00:00'); const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((d - today) / 86400000);
  if (days <= 0) return { text: days === 0 ? 'due today' : `due ${-days}d ago`, hue: 'rose' };
  if (days <= 30) return { text: `due in ${days}d`, hue: 'amber' };
  return { text: `revisit ${d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}`, hue: 'slate' };
};

// Hold signals pass a model's evidence read (app/hold_evidence.py). The card
// shows that verdict's work: the reason, the quote and the record it is from,
// and the steps — fetched when it opens (lists carry no steps).
const HOLD_CONF = { high: 'green', medium: 'amber', low: 'slate' };
const _noEscape = () => {};
const HoldWhyCard = ({ s, at, onClose, onOpenNote }) => {
  const [full, setFull] = React.useState(null);
  const [err, setErr] = React.useState(null);
  (window.useEscapeKey || _noEscape)(true, onClose);
  React.useEffect(() => {
    let alive = true;
    window.AtlasApi.companySignal(s.id)
      .then((r) => { if (alive) setFull(r); })
      .catch((e) => { if (alive) setErr(e.message || 'Could not load'); });
    return () => { alive = false; };
  }, [s.id]);
  const ev = (full && full.evidence) || s.evidence || {};
  const steps = (full && full.evidence && full.evidence.steps) || [];
  const Steps = window.ResearchSteps;
  const kind = signalKind(s.kind);
  const width = Math.min(420, window.innerWidth - 24);
  const top = Math.max(12, Math.min(at.y + 12, window.innerHeight - 460));
  const heading = (t) => (
    <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
      color: 'var(--text-4)', margin: '10px 0 4px' }}>{t}</div>
  );
  const src = [ev.source_kind, ev.source_from, ev.source_date && window.fmtDate ? window.fmtDate(ev.source_date) : ev.source_date]
    .filter(Boolean).join(' · ');
  return ReactDOM.createPortal(
    <>
      <div onClick={(e) => { e.stopPropagation(); onClose(); }} style={{ position: 'fixed', inset: 0, zIndex: 400 }} />
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-label={`${kind.label} · how it got here`} style={{
        position: 'fixed', left: Math.max(12, Math.min(at.x - 20, window.innerWidth - width - 12)), top, zIndex: 401,
        width, maxHeight: Math.min(560, window.innerHeight - top - 12), overflowY: 'auto',
        background: 'var(--surface)', border: '1px solid var(--border-strong)', borderRadius: 10,
        boxShadow: 'var(--shadow-md, var(--shadow-sm))', padding: '10px 12px 12px',
        fontSize: 12, lineHeight: 1.5, color: 'var(--text-2)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          {window.Icon && window.Icon.Wand && <window.Icon.Wand size={13} color="var(--accent-text)" />}
          <span style={{ fontWeight: 600, color: 'var(--text)' }}>{kind.label}</span>
          <span style={{ flex: 1 }} />
          <button onClick={onClose} title="Close" style={{ border: 'none', background: 'transparent',
            cursor: 'pointer', color: 'var(--text-3)', padding: 2, display: 'inline-flex' }}>
            {window.Icon && window.Icon.Close ? <window.Icon.Close size={12} /> : '×'}
          </button>
        </div>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{s.title}</div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
          {ev.reach_out_now && <window.Pill hue="green" sz="sm">Reach out now</window.Pill>}
          {ev.confidence && <window.Pill hue={HOLD_CONF[ev.confidence] || 'slate'} sz="sm">{ev.confidence} confidence</window.Pill>}
        </div>
        {ev.reason && <div style={{ marginTop: 8 }}>{ev.reason}</div>}
        {ev.quote && (
          <>
            {heading('Source')}
            <div style={{ color: 'var(--text-3)', fontStyle: 'italic', wordBreak: 'break-word' }}>“{ev.quote}”</div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap', marginTop: 2, fontSize: 11.5, color: 'var(--text-4)' }}>
              {src && <span>{src}</span>}
              {ev.source_note_id && (
                <button onClick={() => onOpenNote(ev.source_note_id)} style={{ background: 'transparent', border: 'none', padding: 0,
                  cursor: 'pointer', color: 'var(--accent-text)', fontSize: 'inherit' }}>open the note →</button>
              )}
            </div>
          </>
        )}
        {Array.isArray(ev.touches) && ev.touches.length > 0 && (
          <>
            {heading(`Unanswered · ${ev.n_touches || ev.touches.length}`)}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {ev.touches.map((t, i) => (
                <div key={`${t.at}-${t.channel}-${i}`} style={{ display: 'flex', gap: 8, alignItems: 'baseline', minWidth: 0 }}>
                  <span className="num" style={{ color: 'var(--text-4)', flexShrink: 0, width: 46 }}>{shortDay(t.at)}</span>
                  <span style={{ fontWeight: 600, color: 'var(--text)', flexShrink: 0 }}>{TOUCH_LABEL[t.channel] || t.channel}</span>
                  <span title={[t.title, t.by].filter(Boolean).join(' · ')}
                    style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-3)' }}>
                    {[t.title, t.by].filter(Boolean).join(' · ')}
                  </span>
                  {t.note_id && (
                    <button onClick={() => onOpenNote(t.note_id)} title="Open the note" aria-label="Open the note"
                      style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent-text)', fontSize: 'inherit', flexShrink: 0 }}>→</button>
                  )}
                </div>
              ))}
            </div>
            {ev.last_reply && (
              <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--text-4)' }}>
                Last reply {window.fmtDate ? window.fmtDate(ev.last_reply.at) : ev.last_reply.at}
                {` · ${TOUCH_LABEL[ev.last_reply.channel] || ev.last_reply.channel}`}
                {ev.last_reply.title ? ` · ${ev.last_reply.title}` : ''}
              </div>
            )}
          </>
        )}
        {heading(`How it got here${steps.length ? ` · ${steps.length} steps` : ''}`)}
        {err && <div style={{ color: 'var(--rose-fg)' }}>{err}</div>}
        {!full && !err && (window.Skeleton ? <window.Skeleton w="70%" h={10} style={{ display: 'block' }} /> : null)}
        {full && steps.length > 0 && Steps && <Steps steps={steps} />}
        {full && steps.length === 0 && <div style={{ color: 'var(--text-4)' }}>No steps recorded</div>}
      </div>
    </>,
    document.body,
  );
};

// The hold signal's two links: the record the quote came from, and the card.
const HoldEvidenceLinks = ({ s, fontSize }) => {
  const [noteId, setNoteId] = React.useState(null);
  const [at, setAt] = React.useState(null);
  const ev = s.evidence || {};
  const sourceId = ev.source_note_id || ev.note_id;
  const link = { background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent-text)', fontSize: fontSize || 'inherit' };
  const openNote = (id) => {
    if (window.openRecordNote(id, { via: 'button' })) return;   // on a record page: ?note=
    if (window.NoteModal) setNoteId(id);
    else window.location.hash = `#/notes/${id}`;
  };
  return (
    <>
      {sourceId && <button onClick={() => openNote(sourceId)} style={link}>open the note →</button>}
      <button onClick={(e) => setAt({ x: e.clientX, y: e.clientY })} style={link}>✨ How it got here</button>
      {at && <HoldWhyCard s={s} at={at} onClose={() => setAt(null)} onOpenNote={(id) => { setAt(null); openNote(id); }} />}
      {noteId && window.NoteModal && <window.NoteModal noteId={noteId} onClose={() => setNoteId(null)} />}
    </>
  );
};

// `onOpenNearby` (record-page redesign step 6, v2 only): a nearby sale links
// to the Market workspace's Nearby tab.
const SignalRow = ({ s, onStatus, compact = false, onOpenNearby }) => {
  // Phone: Done / Dismiss move under the text as full-size buttons (beside
  // it they squeeze the title to a sliver), and the source links get a
  // finger-sized row. Desktop and iPad keep the side column.
  const phone = !!window.useDevice().phone;
  const kind = signalKind(s.kind); const due = dueLabel(s.due_on);
  const ev = s.evidence || {};
  const hold = isHoldKind(s.kind);
  const why = hasWhyCard(s);
  return (
    <div style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)', alignItems: 'flex-start', ...(phone ? { flexDirection: 'column', alignItems: 'stretch', padding: '10px 0' } : {}) }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <window.Pill hue={kind.hue} sz="sm">{kind.label}</window.Pill>
          {due && <window.Pill hue={due.hue} sz="sm">{due.text}</window.Pill>}
          {s.strength === 'high' && <window.Pill hue="green" sz="sm">strong</window.Pill>}
          <span style={{ fontSize: phone ? 15 : 12.5, fontWeight: 600 }}>{s.title}</span>
        </div>
        {!compact && s.detail && (
          <div style={{ fontSize: phone ? 14 : 12, color: 'var(--text-3)', marginTop: 3, lineHeight: 1.45 }}>{s.detail}</div>
        )}
        {!compact && hold && ev.quote && (
          <div style={{ fontSize: phone ? 14 : 12, color: 'var(--text-3)', marginTop: 2, lineHeight: 1.45, fontStyle: 'italic' }}>“{ev.quote}”</div>
        )}
        <div style={{ fontSize: 10.5, color: 'var(--text-4)', marginTop: 3, display: 'flex', gap: 10, ...(phone ? { fontSize: 13, gap: 16, flexWrap: 'wrap', alignItems: 'center', minHeight: 32 } : {}) }}>
          {s.occurred_at && <span>{hold ? `${SIGNAL_WHEN[s.kind]} ` : ''}{window.fmtDate ? window.fmtDate(s.occurred_at) : s.occurred_at}</span>}
          {ev.url && <a href={ev.url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)', ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>{window.PhoneHit && <window.PhoneHit />}source ↗</a>}
          {why && <HoldEvidenceLinks s={s} fontSize={phone ? 13 : 10.5} />}
          {ev.engagement_id && (
            <button onClick={() => window.AtlasNav?.goToDeal?.(ev.engagement_id)}
              style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent)', fontSize: phone ? 13 : 10.5, ...(phone ? { minHeight: 32 } : {}), ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>
              {window.PhoneHit && <window.PhoneHit />}
              open the deal →
            </button>
          )}
          {onOpenNearby && s.kind === 'nearby_sale' && (
            <button type="button" onClick={onOpenNearby}
              style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent)', fontSize: phone ? 13 : 10.5, ...(phone ? { minHeight: 32 } : {}) }}>
              Nearby ›
            </button>
          )}
        </div>
      </div>
      {onStatus && (phone ? (
        <div style={{ display: 'flex', gap: 8 }}>
          <window.Btn size="sm" variant="outline" icon={window.Icon && <window.Icon.Check size={13} />} onClick={() => onStatus(s, 'acted')} style={{ flex: 1, justifyContent: 'center' }}>Done — handled</window.Btn>
          <window.Btn size="sm" variant="ghost" onClick={() => onStatus(s, 'dismissed')} style={{ flex: 1, justifyContent: 'center' }}>Dismiss</window.Btn>
        </div>
      ) : (
        <div style={{ display: 'inline-flex', gap: 4, flexShrink: 0 }}>
          <window.Btn size="xs" variant="ghost" title="Handled — a call or task followed" onClick={() => onStatus(s, 'acted')}>Done</window.Btn>
          <window.Btn size="xs" variant="ghost" title="Not useful for this company" onClick={() => onStatus(s, 'dismissed')}>Dismiss</window.Btn>
        </div>
      ))}
    </div>
  );
};

// `marketHere` (step 6): the page's Market workspace applies (a seller
// company), so a nearby sale links to its Nearby tab. Default off.
const CompanySignalsSection = ({ companyId, sectionId = 'company.signals', marketHere = false }) => {
  const [items, setItems] = React.useState(null);
  const load = React.useCallback(() => {
    if (!companyId) return;
    window.AtlasApi.companySignals(companyId).then((r) => setItems(r.items || [])).catch(() => setItems([]));
  }, [companyId]);
  React.useEffect(() => { load(); }, [load]);
  const onStatus = async (s, status) => {
    try {
      await window.AtlasApi.setCompanySignalStatus(s.id, status);
      setItems((prev) => (prev || []).filter((x) => x.id !== s.id));
    } catch (e) {
      window.pushToast?.({ type: 'error', title: 'Could not update signal', body: e.message });
    }
  };
  if (!items || items.length === 0) return null;
  const due = items.filter((s) => s.due_on && new Date(s.due_on + 'T00:00:00') <= new Date()).length;
  // v2: a nearby sale opens the Market workspace's Nearby tab (step 6), on a
  // page where that workspace applies (PanelHost strips it on a non-seller).
  const onOpenNearby = marketHere && typeof window.recordPanelAvailable === 'function' && window.recordPanelAvailable('market', window.recordSurface())
    ? () => window.openRecordPanel('market', { tab: 'nearby' }, { via: 'button' }) : undefined;
  return (
    <div style={{ marginBottom: 14 }}>
      <window.Section
        title={`Call triggers · ${items.length}`}
        collapsible id={sectionId} defaultOpen
        preview={`${due ? `${due} due · ` : ''}${items.slice(0, 2).map((s) => signalKind(s.kind).label).join(', ')}`}
      >
        {items.map((s) => <SignalRow key={s.id} s={s} onStatus={onStatus} onOpenNearby={onOpenNearby} />)}
      </window.Section>
    </div>
  );
};

Object.assign(window, { CompanySignalsSection, SignalRow, SIGNAL_KIND, signalKind, signalDueLabel: dueLabel });
// The hold evidence links and card, for any page that lists a hold signal.
Object.assign(window, { HoldEvidenceLinks, HoldWhyCard });

// ── The ranked queue (Signals › Companies) ─────────────────────────────────
// One card per company, ranked by its live signals: due revisits first, then
// strong ones, then the rest by recency. The filter chips say how many
// companies each would show. Done / Dismiss take a signal off the list at
// once and the toast that follows offers Undo; "Show more" pages through the
// rest. The page scrolls itself (signals.jsx), so the toolbar sticks to its top.
const QUEUE_PAGE = 50;     // companies per request
const QUEUE_SHOWN = 3;     // signals a card shows before "+ N more"
const SCOPE_KEY = 'atlas.signals.scope';
const SIGNAL_HELP = {
  hold_reengaged: 'Someone at a company whose deal is on hold emailed us in the last 30 days.',
  hold_checkin:   'An on-hold deal past its check-in date whose notes or emails say it is time to reach back out.',
  timing_note:    'An owner told us when they would be ready — or said "later" and the revisit date has come.',
  went_dark:      'A live deal past the first call that nobody has written a note on in a year — or an owner who replied before and has left our last 3 emails, calls or texts unanswered.',
  nearby_sale:    'A competitor in the same trade and town just sold — the opener for a call.',
  nearby_listing: 'A business in their trade and area is for sale — "someone near you is selling."',
  buyer_demand:   'Buyers are acquiring in this industry right now.',
  web_visit:      'Someone from a company with an open deal, in a campaign, or on a call list visited industrypro.com.',
};
// What each signal's date is the date of — a bare date on a card means nothing.
const SIGNAL_WHEN = { timing_note: 'Call', went_dark: 'Last touch', nearby_sale: 'Sold', nearby_listing: 'Listed',
  hold_reengaged: 'Wrote', hold_checkin: 'Last touch', web_visit: 'Visited' };

// The row logic (what a due date reads as, taking signals off and putting them
// back) lives in signal_queue_logic.jsx, where `node --test` can reach it.
const { rowKey, queueDue, signalHead, groupByKind, dropSignals, restoreSignals } = window.SignalQueueLogic;
// Whose signals: 'mine', 'all', one dealmaker's id, or 'none' (no owner found).
const BOOK_RE = /^(mine|all|none|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
const readScope = () => { try { const v = localStorage.getItem(SCOPE_KEY) || ''; return BOOK_RE.test(v) ? v : 'mine'; } catch (_) { return 'mine'; } };
const bookParams = (book) => (book === 'mine' || book === 'all' ? { scope: book } : { scope: 'all', owner_id: book });
const writeScope = (v) => { try { localStorage.setItem(SCOPE_KEY, v); } catch (_) { /* private window — the choice just isn't remembered */ } };
const scrollParent = (el) => {
  for (let p = el && el.parentElement; p; p = p.parentElement) {
    const o = getComputedStyle(p).overflowY;
    if (o === 'auto' || o === 'scroll') return p;
  }
  return null;
};

// One signal: what happened, the owner's own words or the suggested opener,
// when, and Done / Dismiss. The buttons show on hover / focus (always on touch).
const QueueSignal = ({ s, row, touch, onAct }) => {
  const [on, setOn] = React.useState(false);
  const [wide, setWide] = React.useState(false);        // the clamped detail, shown in full
  const [head, reason] = signalHead(s);
  const due = queueDue(s.due_on);
  const ev = s.evidence || {};
  const when = SIGNAL_WHEN[s.kind] && s.occurred_at ? `${SIGNAL_WHEN[s.kind]} ${window.fmtDate(s.occurred_at)}` : null;
  const showDeal = ev.engagement_id && ev.engagement_id !== row.engagement_id;
  const hold = isHoldKind(s.kind);
  const why = hasWhyCard(s);
  const actions = (
    <div style={{ display: 'inline-flex', gap: 4, flexShrink: 0, transition: 'opacity .12s',
      opacity: touch || on ? 1 : 0, ...(touch ? { gap: 8 } : {}) }}>
      <window.Btn size="xs" variant="outline" title="Handled — a call or task followed" onClick={() => onAct([s], 'acted')}
        style={touch ? { flex: 1, justifyContent: 'center' } : undefined}>Done</window.Btn>
      <window.Btn size="xs" variant="ghost" title="Not useful for this company" onClick={() => onAct([s], 'dismissed')}
        style={touch ? { flex: 1, justifyContent: 'center' } : undefined}>Dismiss</window.Btn>
    </div>
  );
  return (
    <div onMouseEnter={() => setOn(true)} onMouseLeave={() => setOn(false)} onFocus={() => setOn(true)} onBlur={() => setOn(false)}
      style={{ display: 'flex', gap: 12, alignItems: 'flex-start', ...(touch ? { flexDirection: 'column', alignItems: 'stretch', gap: 8 } : {}) }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: touch ? 14.5 : 13, lineHeight: 1.5, color: 'var(--text)' }}>
          <span style={{ fontWeight: 600 }}>{head}</span>
          {reason && <span style={{ color: 'var(--text-2)' }}> · {reason}</span>}
          {due && <span style={{ marginLeft: 8, verticalAlign: 1 }}><window.Pill hue={due.hue} sz="sm">{due.text}</window.Pill></span>}
        </div>
        {s.detail && (
          <div onClick={() => setWide(!wide)} title={wide ? 'Show less' : 'Show all'}
            style={{ fontSize: touch ? 13.5 : 12, lineHeight: 1.5, color: 'var(--text-3)', marginTop: 1, cursor: 'pointer',
              fontStyle: s.kind === 'timing_note' ? 'italic' : 'normal',
              ...(wide ? {} : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }) }}>
            {s.kind === 'timing_note' ? `“${s.detail}”` : s.detail}
          </div>
        )}
        {hold && ev.quote && (
          <div onClick={() => setWide(!wide)} title={wide ? 'Show less' : 'Show all'}
            style={{ fontSize: touch ? 13.5 : 12, lineHeight: 1.5, color: 'var(--text-3)', marginTop: 1, cursor: 'pointer', fontStyle: 'italic',
              ...(wide ? {} : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }) }}>
            “{ev.quote}”
          </div>
        )}
        {(when || ev.url || showDeal || why) && (
          <div style={{ fontSize: touch ? 13 : 11.5, color: 'var(--text-4)', marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {when && <span>{when}</span>}
            {ev.url && <a href={ev.url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent-text)', ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>{window.PhoneHit && <window.PhoneHit />}source ↗</a>}
            {why && <HoldEvidenceLinks s={s} />}
            {showDeal && (
              <button onClick={() => window.AtlasNav?.goToDeal?.(ev.engagement_id)}
                style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent-text)', fontSize: 'inherit', ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>
                {window.PhoneHit && <window.PhoneHit />}
                open the deal →
              </button>
            )}
          </div>
        )}
      </div>
      {actions}
    </div>
  );
};

// One company: who, then why now. Signals of a kind share one label; a card
// shows its first few and tucks the rest under "+ N more".
const QueueCard = ({ row, scope, touch, goToCompany, goToDeal, onAct }) => {
  const [on, setOn] = React.useState(false);
  const [all, setAll] = React.useState(false);
  const sigs = row.signals || [];
  const shown = all ? sigs : sigs.slice(0, QUEUE_SHOWN);
  const place = [row.city, row.state].filter(Boolean).join(', ');
  const kindTotal = (kind) => sigs.filter((x) => x.kind === kind).length;   // ×N counts them all, shown or tucked away
  return (
    <div onMouseEnter={() => setOn(true)} onMouseLeave={() => setOn(false)} onFocus={() => setOn(true)} onBlur={() => setOn(false)}
      style={{ border: '1px solid var(--border)', borderRadius: 12, background: 'var(--surface)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '12px 16px 10px' }}>
        <a href={`#/companies/${row.company_id}`} title={`Priority score ${row.score.toFixed(1)} — higher ranks first`}
          onClick={(e) => { if (e.metaKey || e.ctrlKey || e.shiftKey || e.button) return; e.preventDefault(); goToCompany && goToCompany(row.company_id); }}
          style={{ fontSize: touch ? 16 : 14.5, fontWeight: 650, color: 'var(--text)', textDecoration: 'none' }}
          onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
          onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}>
          {row.name}
        </a>
        {row.pipeline_state === 'active' && <window.Pill hue="green" sz="sm" title="Has a live deal">active</window.Pill>}
        <span style={{ fontSize: touch ? 13.5 : 12, color: 'var(--text-3)' }}>
          {[row.industry, place].filter(Boolean).join(' · ')}
        </span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {scope === 'all' && row.owner_name && <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{row.owner_name}</span>}
          {sigs.length > 1 && (
            <span style={{ opacity: touch || on ? 1 : 0, transition: 'opacity .12s' }}>
              <window.Btn size="xs" variant="ghost" title="Mark every signal on this company handled" onClick={() => onAct(sigs, 'acted', row)}>
                Done with all {sigs.length}
              </window.Btn>
            </span>
          )}
          {row.engagement_id && goToDeal && (
            <window.Btn size="xs" variant="ghost" onClick={() => goToDeal(row.engagement_id)}>Open deal</window.Btn>
          )}
        </span>
      </div>
      <div style={{ padding: '0 16px' }}>
        {groupByKind(shown).map(([kind, list]) => {
          const k = signalKind(kind);
          return (
            <div key={kind} style={{ display: 'grid', gridTemplateColumns: touch ? 'minmax(0, 1fr)' : '116px minmax(0, 1fr)',
              gap: touch ? 6 : 12, padding: '10px 0', borderTop: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <window.Pill hue={k.hue} sz="sm" title={SIGNAL_HELP[kind]}>{k.label}</window.Pill>
                {kindTotal(kind) > 1 && <span style={{ fontSize: 11, color: 'var(--text-4)', lineHeight: '19px' }}>×{kindTotal(kind)}</span>}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
                {list.map((s) => <QueueSignal key={s.id} s={s} row={row} touch={touch} onAct={(sg, status) => onAct(sg, status, row)} />)}
              </div>
            </div>
          );
        })}
        {sigs.length > QUEUE_SHOWN && (
          <div style={{ borderTop: '1px solid var(--border)', padding: '6px 0' }}>
            <window.Btn size="xs" variant="ghost" onClick={() => setAll(!all)}>
              {all ? 'Show fewer' : `+ ${sigs.length - QUEUE_SHOWN} more signal${sigs.length - QUEUE_SHOWN === 1 ? '' : 's'}`}
            </window.Btn>
          </div>
        )}
      </div>
    </div>
  );
};

const QueueSkeleton = () => (
  <div style={{ border: '1px solid var(--border)', borderRadius: 12, background: 'var(--surface)', padding: '14px 16px' }}>
    <window.Skeleton w={220} h={14} style={{ display: 'block' }} />
    <window.Skeleton w={150} h={10} style={{ display: 'block', marginTop: 8 }} />
    <window.Skeleton w="78%" h={12} style={{ display: 'block', marginTop: 18 }} />
    <window.Skeleton w="52%" h={10} style={{ display: 'block', marginTop: 8 }} />
  </div>
);

const QueueHelp = ({ onClose }) => (
  <div style={{ border: '1px solid var(--border)', borderRadius: 12, background: 'var(--surface)', padding: '14px 16px', marginTop: 12 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ fontSize: 13, fontWeight: 650, color: 'var(--text)' }}>How the queue works</div>
      <span style={{ flex: 1 }} />
      <window.Btn size="xs" variant="ghost" onClick={onClose}>Close</window.Btn>
    </div>
    <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.55, marginTop: 6 }}>
      Each signal is scored — owner timing counts most, an overdue revisit counts extra, and strong, recent signals beat
      weak, old ones — and companies are listed by their total. Atlas refreshes signals every night from call notes,
      live deals, market sales, listings and buyer activity. <b>Done</b> means you handled it; <b>Dismiss</b> means it isn't useful for that company.
      Either takes it off the queue, and Undo is offered right away.
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '10px 18px', marginTop: 12 }}>
      {Object.entries(SIGNAL_KIND).map(([k, v]) => (
        <div key={k} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <window.Pill hue={v.hue} sz="sm">{v.label}</window.Pill>
          <span style={{ fontSize: 12, color: 'var(--text-3)', lineHeight: 1.45 }}>{SIGNAL_HELP[k]}</span>
        </div>
      ))}
    </div>
  </div>
);

// Phone (signals.jsx): `resume` is the queue as it was left, on the way Back
// from a company opened from it; `onKeep` takes the state to leave behind.
const CompanySignalQueue = ({ goToCompany, goToDeal, resume, onKeep }) => {
  const dev = window.useDevice();
  const phone = !!dev.phone;
  const touch = !!dev.touch;
  const [was] = React.useState(() => resume || null);
  const kept = !!(was && was.data);
  const [scope, setScope] = React.useState(() => (was ? was.scope : readScope()));
  const [lens, setLens] = React.useState(was ? was.lens : { due: false, kinds: [] });   // "Due now" and the kind chips are one choice
  const [text, setText] = React.useState(was ? was.text : '');
  const [q, setQ] = React.useState(was ? was.q : '');                                // text, once typing pauses
  const [industry, setIndustry] = React.useState(was && Array.isArray(was.industry) ? was.industry : []);   // industry ids: any of them
  const [industries, setIndustries] = React.useState([]);
  const [data, setData] = React.useState(kept ? was.data : { items: null, total: null, facets: null });
  const [loading, setLoading] = React.useState(!kept);
  const [paging, setPaging] = React.useState(false);
  const [exhausted, setExhausted] = React.useState(kept ? was.exhausted : false);
  const [error, setError] = React.useState(null);
  const [help, setHelp] = React.useState(false);
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const [members, setMembers] = React.useState([]);     // names for a picked dealmaker with nothing under these filters
  const [canExport, setCanExport] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const toolRef = React.useRef(null);
  const seq = React.useRef(0);          // the newest list request wins
  const metaSeq = React.useRef(0);      // …and the newest count refresh
  const undoToast = React.useRef(null); // one Undo on screen at a time
  const first = React.useRef(true);

  const params = React.useMemo(() => {
    const p = bookParams(scope);
    if (lens.kinds.length) p.kinds = lens.kinds.join(',');
    if (lens.due) p.due_only = true;
    if (q) p.q = q;
    if (industry.length) p.industry_id = industry.join(',');
    return p;
  }, [scope, lens, q, industry]);
  const filtered = !!(q || industry.length || lens.due || lens.kinds.length);

  React.useEffect(() => { const t = setTimeout(() => setQ(text.trim()), 250); return () => clearTimeout(t); }, [text]);
  React.useEffect(() => {
    window.AtlasApi.listIndustries()
      .then((r) => setIndustries((Array.isArray(r) ? r : []).filter((i) => i.active !== false)
        .map((i) => ({ value: i.id, label: i.name })).sort((a, b) => a.label.localeCompare(b.label))))
      .catch(() => setIndustries([]));
    window.AtlasApi.listTeamMembers().then((r) => setMembers(Array.isArray(r) ? r : [])).catch(() => setMembers([]));
    window.AtlasApi.authPermissions?.().then((r) => {
      const perm = ((r && r.permissions) || []).find((x) => x.key === 'export_data');
      setCanExport(!!(perm && perm.allowed));
    }).catch(() => {});
  }, []);

  const load = React.useCallback(() => {
    const mine = ++seq.current;
    setLoading(true);
    window.AtlasApi.companySignalQueue({ ...params, limit: QUEUE_PAGE, offset: 0 })
      .then((r) => {
        if (mine !== seq.current) return;
        const items = r.items || [];
        setData({ items, total: r.total ?? items.length, facets: r.facets || null });
        setExhausted(items.length < QUEUE_PAGE);
        setError(null);
      })
      .catch((e) => {
        if (mine !== seq.current) return;
        setError(e.message || 'Could not load signals');
        setData((d) => ({ ...d, items: d.items || [] }));
      })
      .finally(() => { if (mine === seq.current) setLoading(false); });
  }, [params]);
  // Kept rows (Back from a company) show as they were; the stretch already
  // loaded is re-read underneath without dimming, so a signal handled on the
  // company page drops out and the scroll position still fits.
  const reread = React.useRef(kept);
  React.useEffect(() => {
    if (!reread.current) { load(); return; }
    reread.current = false;
    const mine = ++seq.current;
    const had = (data.items || []).length;
    const n = Math.min(200, Math.max(QUEUE_PAGE, had));
    window.AtlasApi.companySignalQueue({ ...params, limit: n, offset: 0 })
      .then((r) => {
        if (mine !== seq.current) return;
        const fresh = r.items || [];
        const seen = new Set(fresh.map(rowKey));
        setData((d) => {
          const items = [...fresh, ...(d.items || []).slice(n).filter((x) => !seen.has(rowKey(x)))];
          return { items, total: r.total ?? items.length, facets: r.facets || d.facets };
        });
        // A list read to its end re-reads at exactly its length: the total says it is still the end.
        if (had <= n) setExhausted(fresh.length < n || (r.total != null && fresh.length >= r.total));
      })
      .catch(() => {});
  }, [load]);   // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (onKeep) onKeep({ scope, lens, text, q, industry, data: loading || error ? null : data, exhausted });
  }, [onKeep, scope, lens, text, q, industry, data, loading, error, exhausted]);
  // A new filter starts a new list: back to the top of it.
  React.useEffect(() => {
    if (first.current) { first.current = false; return; }
    const sp = scrollParent(toolRef.current);
    if (sp) sp.scrollTo({ top: 0 });
  }, [params]);

  const loadMore = () => {
    if (paging || !data.items) return;
    const mine = seq.current;
    setPaging(true);
    window.AtlasApi.companySignalQueue({ ...params, limit: QUEUE_PAGE, offset: data.items.length })
      .then((r) => {
        if (mine !== seq.current) return;
        const more = r.items || [];
        setData((d) => {
          const seen = new Set((d.items || []).map(rowKey));
          return { ...d, items: [...d.items, ...more.filter((x) => !seen.has(rowKey(x)))], total: r.total ?? d.total };
        });
        setExhausted(more.length < QUEUE_PAGE);
      })
      .catch((e) => window.pushToast?.({ type: 'error', title: 'Could not load more', body: e.message }))
      .finally(() => setPaging(false));
  };

  // The header numbers move when a signal does; ask for them quietly (one row).
  // A reply for filters the person has since left is dropped (seq moved on).
  const refreshMeta = () => {
    const mine = ++metaSeq.current;
    const list = seq.current;
    window.AtlasApi.companySignalQueue({ ...params, limit: 1, offset: 0 })
      .then((r) => {
        if (mine === metaSeq.current && list === seq.current) setData((d) => ({ ...d, total: r.total ?? d.total, facets: r.facets || d.facets }));
      })
      .catch(() => {});
  };

  const putBack = (row, index, ids) => {
    setData((d) => { const [items, added] = restoreSignals(d.items || [], row, index, ids); return { ...d, items, total: (d.total ?? 0) + added }; });
  };
  // `list` is the list generation the action came from: once the person has moved
  // to other filters, the rows on screen are not the ones to put anything back into.
  const undo = (row, index, sigs, list) => {
    const ids = new Set(sigs.map((s) => s.id));
    const here = list === seq.current;
    if (here) putBack(row, index, ids);
    Promise.all(sigs.map((s) => window.AtlasApi.setCompanySignalStatus(s.id, 'new')))
      .then(refreshMeta)
      .catch((e) => {
        window.pushToast?.({ type: 'error', title: 'Could not undo', body: e.message });
        if (here && list === seq.current) {
          setData((d) => { const [items, gone] = dropSignals(d.items || [], ids); return { ...d, items, total: Math.max(0, (d.total ?? 0) - gone) }; });
        }
      });
  };
  // Optimistic: the signals leave the list now; a failed write puts them back.
  const act = (sigs, status, row) => {
    const ids = new Set(sigs.map((s) => s.id));
    const index = (data.items || []).findIndex((r) => rowKey(r) === rowKey(row));
    const snapshot = { ...row, signals: row.signals.slice() };
    const list = seq.current;
    setData((d) => { const [items, gone] = dropSignals(d.items || [], ids); return { ...d, items, total: Math.max(0, (d.total ?? 0) - gone) }; });
    Promise.all(sigs.map((s) => window.AtlasApi.setCompanySignalStatus(s.id, status)))
      .then(() => {
        refreshMeta();
        if (undoToast.current) window.dismissToast?.(undoToast.current);
        undoToast.current = window.pushToast?.({
          type: 'success', duration: 7000,
          title: status === 'acted' ? 'Marked done' : 'Dismissed',
          body: sigs.length === 1 ? `${row.name} · ${signalHead(sigs[0])[0]}` : `${row.name} · ${sigs.length} signals`,
          action: { label: 'Undo', onClick: () => undo(snapshot, index, sigs, list) },
        });
      })
      .catch((e) => {
        if (list === seq.current) putBack(snapshot, index, ids);
        window.pushToast?.({ type: 'error', title: 'Could not update the signal', body: e.message });
      });
  };

  const items = data.items;
  const F = data.facets;
  const total = data.total;
  const left = total != null && items ? Math.max(0, total - items.length) : null;
  const pickScope = (v) => { setScope(v); writeScope(v); };
  const person = scope !== 'mine' && scope !== 'all';
  // Dealmakers with live signals under the other filters, busiest first; a
  // picked one with none still shows (by name), and so does "No owner".
  const owners = (F && F.owners) || [];
  const nameOf = (id) => (owners.find((o) => o.id === id) || {}).name || (members.find((m) => m.id === id) || {}).full_name;
  const bookOptions = (() => {
    const people = owners.filter((o) => o.id !== 'none');
    if (person && scope !== 'none' && !people.some((o) => o.id === scope)) people.push({ id: scope, name: nameOf(scope) || 'Dealmaker', companies: 0, active: true });
    const nobody = owners.find((o) => o.id === 'none') || (scope === 'none' ? { companies: 0 } : null);
    const n = (o) => (o.companies || 0).toLocaleString();
    return [
      { value: 'mine', label: 'Mine' }, { value: 'all', label: 'Everyone' },
      ...(people.length ? [{ header: 'Dealmaker' }] : []),
      ...people.map((o) => ({ value: o.id, label: o.name || 'Unknown', hint: o.active ? n(o) : `inactive · ${n(o)}` })),
      ...(nobody ? [{ value: 'none', label: 'No owner', hint: n(nobody), description: 'No active dealmaker, company owner or industry lead to route it to' }] : []),
    ];
  })();
  const bookName = scope === 'mine' ? 'mine' : scope === 'all' ? 'everyone' : scope === 'none' ? 'no owner' : (nameOf(scope) || 'dealmaker');
  const exportXlsx = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const { truncated } = await window.AtlasApi.exportCompanySignals(params);
      if (truncated) window.pushToast?.({ type: 'warning', title: 'Export hit the row cap', body: 'Narrow the filters to get every company.' });
    } catch (e) {
      window.pushToast?.({ type: 'error', title: 'Export failed', body: e.message });
    } finally { setExporting(false); }
  };
  const clearFilters = () => { setLens({ due: false, kinds: [] }); setText(''); setQ(''); setIndustry([]); };
  const chip = (active, tone) => ({
    fontSize: 12, padding: '4px 11px', borderRadius: 999, border: '1px solid', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
    borderColor: active ? (tone === 'due' ? 'var(--rose-fg)' : 'var(--text)') : (tone === 'due' ? 'transparent' : 'var(--border)'),
    background: active ? (tone === 'due' ? 'var(--rose-fg)' : 'var(--text)') : (tone === 'due' ? 'var(--rose-bg)' : 'var(--surface)'),
    color: active ? 'var(--surface)' : (tone === 'due' ? 'var(--rose-fg)' : 'var(--text-2)'),
    ...(phone ? { fontSize: 14, height: 34, padding: '0 14px' } : {}),
  });
  const chipBtn = (key, label, count, active, onClick, tone, title) => (
    <button key={key} style={{ ...chip(active, tone), opacity: count === 0 && !active ? 0.5 : 1 }} onClick={onClick} title={title} aria-pressed={active}>
      {label}
      {count != null && <span style={{ marginLeft: 6, opacity: 0.7, fontVariantNumeric: 'tabular-nums' }}>{count.toLocaleString()}</span>}
    </button>
  );
  const none = !lens.due && lens.kinds.length === 0;
  const inputH = touch ? 38 : 30;

  return (
    <div>
      {/* Phone: edge to edge, out over the page's 16px gutter (signals.jsx). */}
      <div ref={toolRef} style={{ position: 'sticky', top: 0, zIndex: 5, background: 'var(--bg)', padding: '8px 0 10px', borderBottom: '1px solid var(--border)', ...(phone ? { margin: '0 -16px' } : {}) }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', ...(phone ? { flexWrap: 'nowrap', overflowX: 'auto', scrollbarWidth: 'none', gap: 8, padding: '2px 16px' } : {}) }}>
          {chipBtn('all', 'All', F ? F.all : null, none, () => setLens({ due: false, kinds: [] }), null, 'Every company with a live signal')}
          {chipBtn('due', 'Due now', F ? F.due_now : null, lens.due, () => setLens({ due: !lens.due, kinds: [] }), 'due',
            'Revisit dates, check-ins and unanswered write-ins whose day has come')}
          {Object.entries(SIGNAL_KIND).map(([k, v]) => chipBtn(k, v.label, F ? F.by_kind[k] : null, lens.kinds.includes(k),
            () => setLens((l) => ({ due: false, kinds: l.kinds.includes(k) ? l.kinds.filter((x) => x !== k) : [...l.kinds, k] })), null, SIGNAL_HELP[k]))}
        </div>
        {/* Phone: search stays in view; Mine / Everyone, the industry and the
            help move into the Filters sheet. */}
        {phone ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '0 16px', marginTop: 10 }}>
            <window.PhoneSearchField value={text} onChange={setText} placeholder="Search" style={{ flex: 1, minWidth: 0 }} />
            <window.PhoneFilterButton count={(industry.length ? 1 : 0) + (person ? 1 : 0)} onClick={() => setFiltersOpen(true)} />
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: 320, minWidth: 0 }}>
              <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-4)', display: 'flex', pointerEvents: 'none' }}>
                <window.Icon.Search size={13} />
              </span>
              <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Search companies…" aria-label="Search companies"
                onKeyDown={(e) => { if (e.key === 'Escape') setText(''); }}
                style={{ width: '100%', boxSizing: 'border-box', height: inputH, padding: '0 28px', fontSize: touch ? 15 : 12.5,
                  border: '1px solid var(--border)', borderRadius: 7, background: 'var(--surface)', color: 'var(--text)' }} />
              {text && (
                <button onClick={() => setText('')} aria-label="Clear search"
                  style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent',
                    color: 'var(--text-3)', cursor: 'pointer', display: 'flex', padding: 2 }}>
                  <window.Icon.Close size={12} />
                </button>
              )}
            </div>
            <window.Picker multiple value={industry} onChange={setIndustry} searchable placeholder="All industries"
              options={industries} minWidth={180} menuMinWidth={260} />
            <span style={{ flex: 1 }} />
            {items && total != null && (
              <span style={{ fontSize: 12, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>
                {items.length < total ? `Showing ${items.length.toLocaleString()} of ${total.toLocaleString()}` : `${total.toLocaleString()} compan${total === 1 ? 'y' : 'ies'}`}
              </span>
            )}
            <window.Btn size="xs" variant="ghost" icon={<window.Icon.Help size={13} />} active={help} onClick={() => setHelp(!help)}>How it works</window.Btn>
            {canExport && (
              <window.Btn size="xs" variant="ghost" icon={<window.Icon.Download size={13} />} disabled={exporting || !total} onClick={exportXlsx}
                title="Every company under these filters, one row per signal (.xlsx)">
                {exporting ? 'Exporting…' : 'Export'}
              </window.Btn>
            )}
            <window.Combobox value={scope} onChange={(e) => pickScope(e.target.value)} searchable placeholder="Mine"
              options={bookOptions} menuMinWidth={240} style={{ minWidth: 150 }} />
          </div>
        )}
      </div>

      {help && !phone && <QueueHelp onClose={() => setHelp(false)} />}

      <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10,
        opacity: loading && items ? 0.55 : 1, pointerEvents: loading && items ? 'none' : 'auto', transition: 'opacity .12s' }}>
        {/* Phone: the count and whose book it is (the scope switch is in the sheet). */}
        {phone && items && items.length > 0 && total != null && (
          <div style={{ fontSize: 13, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>
            {`${total.toLocaleString()} compan${total === 1 ? 'y' : 'ies'} · ${bookName}`}
          </div>
        )}
        {items === null && !error && [0, 1, 2, 3].map((i) => <QueueSkeleton key={i} />)}
        {error && (
          <div style={{ padding: '14px 16px', border: '1px solid var(--border)', borderRadius: 12, background: 'var(--surface)', fontSize: 12.5, color: 'var(--rose-fg)',
            display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ flex: 1 }}>Could not load signals — {error}</span>
            <window.Btn size="xs" variant="outline" onClick={load}>Retry</window.Btn>
          </div>
        )}
        {items && items.length === 0 && !error && (
          <div style={{ padding: '28px 18px', textAlign: 'center', border: '1px solid var(--border)', borderRadius: 12, background: 'var(--surface)' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
              {filtered ? 'No companies match these filters' : scope === 'mine' ? 'Nothing on your book right now'
                : person ? (scope === 'none' ? 'No unowned signals' : `Nothing on ${bookName}'s book right now`) : 'No live signals'}
            </div>
            {filtered && (
              <div style={{ fontSize: 12.5, color: 'var(--text-3)', lineHeight: 1.55, margin: '6px auto 0', maxWidth: 460 }}>
                Try a different kind, or clear the search.
              </div>
            )}
            <div style={{ marginTop: 14 }}>
              {filtered ? <window.Btn size="sm" variant="outline" onClick={clearFilters}>Clear filters</window.Btn>
                : scope !== 'all' ? <window.Btn size="sm" variant="outline" onClick={() => pickScope('all')}>Show everyone</window.Btn> : null}
            </div>
          </div>
        )}
        {(items || []).map((row) => (
          <QueueCard key={rowKey(row)} row={row} scope={scope === 'all' ? 'all' : 'mine'} touch={touch} goToCompany={goToCompany} goToDeal={goToDeal} onAct={act} />
        ))}
        {items && items.length > 0 && !exhausted && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '6px 0 4px' }}>
            <window.Btn size="sm" variant="outline" disabled={paging} onClick={loadMore}>
              {paging ? 'Loading…' : `Show ${left != null ? Math.min(QUEUE_PAGE, left) || QUEUE_PAGE : QUEUE_PAGE} more${left ? ` · ${left.toLocaleString()} left` : ''}`}
            </window.Btn>
          </div>
        )}
        {items && items.length >= QUEUE_PAGE && exhausted && (
          <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-4)', padding: '8px 0 4px' }}>That's everything — {items.length.toLocaleString()} companies</div>
        )}
      </div>

      {phone && (
        <window.PhoneSheet open={filtersOpen} onClose={() => setFiltersOpen(false)} title="Filters">
          <div style={{ padding: '4px 18px 18px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <div style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 6 }}>Owner</div>
              <window.PhoneSegmented value={person ? '' : scope} onChange={pickScope}
                options={[{ value: 'mine', label: 'Mine' }, { value: 'all', label: 'Everyone' }]} />
              <span style={{ position: 'relative', display: 'block', marginTop: 8 }}>
                <select value={person ? scope : ''} onChange={(e) => pickScope(e.target.value || 'all')} aria-label="Dealmaker"
                  style={{ width: '100%', height: 44, fontSize: 16, padding: '0 36px 0 12px', borderRadius: 10, border: '1px solid var(--border-strong)',
                    background: 'var(--surface)', color: 'var(--text)', fontFamily: 'inherit', WebkitAppearance: 'none', appearance: 'none' }}>
                  <option value="">Any dealmaker</option>
                  {bookOptions.filter((o) => o.value && o.value !== 'mine' && o.value !== 'all').map((o) => (
                    <option key={o.value} value={o.value}>{`${o.label} · ${o.hint}`}</option>
                  ))}
                </select>
                <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-3)', display: 'inline-flex' }}>
                  <window.Icon.ChevronD size={14} />
                </span>
              </span>
            </div>
            <div>
              <div style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 6 }}>Industry</div>
              {/* A native picker, as on the Web tab: a Combobox here is a sheet on a sheet. */}
              <span style={{ position: 'relative', display: 'block' }}>
                {/* One industry here; several picked on a desktop read as one row. */}
                <select value={industry.length > 1 ? '*' : (industry[0] || '')} onChange={(e) => setIndustry(e.target.value ? [e.target.value] : [])} aria-label="Industry"
                  style={{ width: '100%', height: 44, fontSize: 16, padding: '0 36px 0 12px', borderRadius: 10, border: '1px solid var(--border-strong)',
                    background: 'var(--surface)', color: 'var(--text)', fontFamily: 'inherit', WebkitAppearance: 'none', appearance: 'none' }}>
                  <option value="">All industries</option>
                  {industry.length > 1 && <option value="*" disabled>{`${industry.length} industries`}</option>}
                  {industries.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
                </select>
                <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-3)', display: 'inline-flex' }}>
                  <window.Icon.ChevronD size={14} />
                </span>
              </span>
            </div>
            <div>
              <window.Btn size="sm" variant="ghost" icon={<window.Icon.Help size={14} />} active={help} onClick={() => setHelp(!help)}>How it works</window.Btn>
              {help && <QueueHelp onClose={() => setHelp(false)} />}
            </div>
          </div>
        </window.PhoneSheet>
      )}
    </div>
  );
};

// ── Home: the signals strip ────────────────────────────────────────────────
// One line under the KPI cards: what is due on the reader's book today.
// Falls back to the team when the reader owns nothing; hidden when empty.
const HomeSignalsStrip = ({ setView }) => {
  const [s, setS] = React.useState(null);
  const [scope, setScope] = React.useState('mine');
  React.useEffect(() => {
    let alive = true;
    window.AtlasApi.companySignalSummary({ scope: 'mine' }).then((mine) => {
      if (!alive) return;
      if (mine && mine.signals > 0) { setS(mine); return; }
      return window.AtlasApi.companySignalSummary({ scope: 'all' }).then((all) => { if (alive) { setS(all); setScope('all'); } });
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!s || !s.signals) return null;
  const parts = [];
  if (s.hold_reengaged) parts.push(`${s.hold_reengaged} held owner${s.hold_reengaged === 1 ? '' : 's'} wrote in`);
  if (s.web_visits) parts.push(`${s.web_visits} warm compan${s.web_visits === 1 ? 'y' : 'ies'} visited the website`);
  if (s.revisits_due) parts.push(`${s.revisits_due} owner${s.revisits_due === 1 ? '' : 's'} said "call me back" and the date has come`);
  if (s.hold_checkins_due) parts.push(`${s.hold_checkins_due} hold check-in${s.hold_checkins_due === 1 ? '' : 's'} due`);
  if (s.revisits_soon) parts.push(`${s.revisits_soon} due in 30 days`);
  if (s.went_dark) parts.push(`${s.went_dark} live deal${s.went_dark === 1 ? '' : 's'} went dark`);
  if (s.nearby_sales) parts.push(`${s.nearby_sales} nearby sale${s.nearby_sales === 1 ? '' : 's'} to mention`);
  if (s.nearby_listings) parts.push(`${s.nearby_listings} nearby listing${s.nearby_listings === 1 ? '' : 's'}`);
  const HUE = window.HUE || {};
  return (
    <button onClick={() => setView('signals')} title="Open Signals › Companies"
      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'var(--surface)',
               border: '1px solid var(--border)', borderRadius: 10, cursor: 'pointer', textAlign: 'left' }}
      onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--border-strong)'}
      onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border)'}>
      <span style={{ width: 24, height: 24, borderRadius: 7, background: (HUE.teal || {}).bg || 'var(--surface-2)', color: (HUE.teal || {}).fg || 'var(--text)',
                     display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {window.Icon && window.Icon.Activity ? <window.Icon.Activity size={13} /> : '◎'}
      </span>
      <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: 'var(--text)' }}>
        <b>{s.signals} signal{s.signals === 1 ? '' : 's'} on {s.companies} compan{s.companies === 1 ? 'y' : 'ies'}</b>
        <span style={{ color: 'var(--text-3)' }}> {scope === 'all' ? 'across the team' : 'on your book'}{parts.length ? ` — ${parts.slice(0, 3).join(' · ')}` : ''}</span>
      </span>
      <span style={{ fontSize: 11.5, color: 'var(--accent)', flexShrink: 0 }}>Open</span>
    </button>
  );
};

// ── Home: the market band ──────────────────────────────────────────────────
// Left: where buyers are moving vs where we are calling, by industry.
// Right: what sellers and buyers said on our calls, and the three multiples
// side by side — said by sellers, indicated by buyers, closed by us.
const REASON_LABEL = {
  retirement: 'Retirement', succession_no_heir: 'No successor', burnout_health: 'Burnout / health', growth_capital: 'Growth capital',
  partner_dispute: 'Partner dispute', market_timing: 'Market timing', diversify_wealth: 'Diversify wealth', not_selling: 'Not selling', unclear: 'Unclear',
};
const CONCERN_LABEL = {
  valuation_gap: 'Valuation gap', confidentiality: 'Confidentiality', employees_legacy: 'Employees & legacy', fees: 'Fees', timing: 'Timing',
  customer_concentration: 'Customer concentration', owner_dependence: 'Owner dependence', earnout_structure: 'Earnout / structure', taxes: 'Taxes',
  trust_advisor: 'Trust in advisor', size_too_small: 'Too small', margins: 'Margins', geography: 'Geography', valuation_too_high: 'Price too high',
  add_backs: 'Add-backs', key_person: 'Key person', growth: 'Growth', industry_fit: 'Industry fit', other: 'Other',
};
const fmtXm = (x) => (x == null ? '—' : `${Number(x).toFixed(1)}×`);

const MarketBand = ({ setView, onOpenDealsTab }) => {
  // Touch (iPad, phone): one column, and the three bar lists wrap instead of
  // splitting ~440px three ways. Desktop keeps the side-by-side band.
  const touch = !!window.useDevice().touch;
  const [t, setT] = React.useState(null);
  React.useEffect(() => { window.AtlasApi.companySignalTrends().then(setT).catch(() => setT({})); }, []);
  const Section = window.Section;
  const sd = (t && t.supply_demand) || [];
  const max = Math.max(1, ...sd.map((r) => Math.max(r.seller_calls, r.buyer_events)));
  const v = (t && t.voice) || {};
  const m = (t && t.multiples) || {};
  const bars = (rows, label) => (rows || []).slice(0, 5).map((r) => (
    <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5 }}>
      <span style={{ width: touch ? 120 : 150, ...(touch ? { flexShrink: 0 } : {}), color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label[r.key] || r.key}</span>
      <span style={{ flex: 1, height: 6, background: 'var(--surface-2)', borderRadius: 3, overflow: 'hidden' }}>
        <span style={{ display: 'block', width: `${Math.round(100 * r.n / Math.max(1, rows[0].n))}%`, height: '100%', background: 'var(--accent)', opacity: 0.7 }} />
      </span>
      <span style={{ width: 28, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text-3)' }}>{r.n}</span>
    </div>
  ));
  const tile = (label, value, sub) => (
    <div style={{ flex: 1, minWidth: 110 }}>
      <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-display)', marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 10.5, color: 'var(--text-4)' }}>{sub}</div>}
    </div>
  );
  return (
    <div style={{ display: 'grid', gridTemplateColumns: touch ? 'minmax(0, 1fr)' : '1fr 1.2fr', gap: 14 }}>
      <Section title={`Where buyers move vs where we call · last ${t?.days || 120} days`}
        action={<window.Btn size="xs" variant="ghost" onClick={() => setView('buyers')}>Buyers</window.Btn>}>
        <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 7 }}>
          {t === null && <div style={{ fontSize: 12, color: 'var(--text-4)' }}>Loading…</div>}
          {t && sd.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>No buyer moves or seller calls filed in this window.</div>}
          {sd.map((r) => (
            <div key={r.industry_id} style={{ fontSize: 11.5 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-2)' }}>
                <span style={{ fontWeight: 600 }}>{r.industry}</span>
                <span style={{ color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{r.seller_calls} seller calls · {r.buyer_events} buyer moves{r.market_sales ? ` · ${r.market_sales} sales` : ''}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 3 }}>
                <span style={{ height: 5, borderRadius: 3, background: 'var(--surface-2)', overflow: 'hidden' }}>
                  <span style={{ display: 'block', height: '100%', width: `${Math.round(100 * r.seller_calls / max)}%`, background: 'var(--accent)', opacity: 0.75 }} />
                </span>
                <span style={{ height: 5, borderRadius: 3, background: 'var(--surface-2)', overflow: 'hidden' }}>
                  <span style={{ display: 'block', height: '100%', width: `${Math.round(100 * r.buyer_events / max)}%`, background: 'oklch(0.65 0.15 300)', opacity: 0.75 }} />
                </span>
              </div>
            </div>
          ))}
          {sd.length > 0 && (
            <div style={{ fontSize: 10.5, color: 'var(--text-4)', display: 'flex', gap: 12, marginTop: 2 }}>
              <span><span style={{ display: 'inline-block', width: 10, height: 5, background: 'var(--accent)', opacity: 0.75, borderRadius: 2 }} /> our seller calls</span>
              <span><span style={{ display: 'inline-block', width: 10, height: 5, background: 'oklch(0.65 0.15 300)', opacity: 0.75, borderRadius: 2 }} /> buyer moves</span>
            </div>
          )}
        </div>
      </Section>
      <Section title="What sellers and buyers are saying · last 12 months"
        action={<window.Btn size="xs" variant="ghost" onClick={() => onOpenDealsTab && onOpenDealsTab('won')}>Closed deals</window.Btn>}>
        <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {tile('Sellers expect', fmtXm(m.said), m.said_n ? `${m.said_n} said a multiple` : 'EBITDA multiple')}
            {tile('Buyers indicate', fmtXm(m.indicated), m.indicated_n ? `${m.indicated_n} indications` : 'EBITDA multiple')}
            {tile('We closed at', fmtXm(m.closed), m.closed_n ? `${m.closed_n} closed deals, median` : 'median')}
            {tile('Calls read', v.n != null ? Number(v.n).toLocaleString() : '—', v.n_revisit_due ? `${v.n_revisit_due} revisit dates due` : (v.n_timing ? `${v.n_timing} with timing` : null))}
          </div>
          {t && (v.n || 0) === 0 && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>No calls read yet.</div>}
          {(v.n || 0) > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: touch ? 'repeat(auto-fit, minmax(220px, 1fr))' : '1fr 1fr 1fr', gap: 14 }}>
              <div>
                <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500, marginBottom: 6 }}>Why sellers talk to us</div>
                {bars(v.seller_reasons, REASON_LABEL)}
              </div>
              <div>
                <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500, marginBottom: 6 }}>Sellers worry about</div>
                {bars(v.seller_concerns, CONCERN_LABEL)}
              </div>
              <div>
                <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500, marginBottom: 6 }}>Buyers push back on</div>
                {bars(v.buyer_concerns, CONCERN_LABEL)}
              </div>
            </div>
          )}
        </div>
      </Section>
    </div>
  );
};

Object.assign(window, { CompanySignalQueue, HomeSignalsStrip, MarketBand });
